import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { errorLog } from "../../utils/logger";
import db, { Key } from "../../database/database";
import { UserInfo } from "../../models/Interfaces";
import { ErrorModal } from "../../components/modals/ErrorModal";
import parsePhoneNumber, { PhoneNumber } from "libphonenumber-js";
import { AsYouType } from "libphonenumber-js";

export const VerifyPhoneSchema = yup.object().shape({
  phone: yup.string().required(),
});

export function VerifyPhone({
  afterSubmit,
  autoFocusInput,
}: {
  afterSubmit?: (phone?: PhoneNumber) => void;
  autoFocusInput?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname, state } = useLocation();

  const [number, setNumber] = useState(null as PhoneNumber | null);
  const [submittingPhoneNumber, setSubmittingPhoneNumber] = useState(false);
  const [formattedNumber, setFormattedNumber] = useState("");
  const [sendFromSettings, setSendFromSettings] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean | string>(false);

  const mobileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocusInput && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [autoFocusInput]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(VerifyPhoneSchema),
  });

  const { ref: registerRef, ...rest } = register("phone");

  const onSendFromSettings = async (data: any) => {
    try {
      setSubmittingPhoneNumber(true);
      const user = await db().getItem<UserInfo>(Key.UserInfo);
      if (user && user.verified_phone_number === number?.nationalNumber) {
        setShowErrorMessage(
          t("onboarding.phone_already_verified", { phoneNumber: formattedNumber })
        );
        return;
      }

      if (!number) {
        return;
      }

      await getAPIClient().addUserAccountPhone({
        phone_number: number.nationalNumber,
      });

      if (afterSubmit) {
        afterSubmit();
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    } finally {
      setSubmittingPhoneNumber(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setSubmittingPhoneNumber(true);
      const user = await db().getItem<UserInfo>(Key.UserInfo);
      if (user && user.verified_phone_number === number?.nationalNumber) {
        setShowErrorMessage(
          t("onboarding.phone_already_verified", { phoneNumber: formattedNumber })
        );
        return;
      }
      if (!number) {
        return;
      }

      await getAPIClient().addUserAccountPhone({
        phone_number: number.nationalNumber,
      });

      if (sendFromSettings || pathname === "/home/settings/phone") {
        if (afterSubmit) {
          afterSubmit(number);
        }
      } else {
        navigate("/signup/enterpin", { state: { phone: number.nationalNumber } });
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    } finally {
      setSubmittingPhoneNumber(false);
    }
  };

  function onTextChange(text: string) {
    /**
     * Allow +1 (NANP) at the front of the phone number, but strip just the + for any zone that is
     * given (eg +44 or +33)
     *
     * https://trello.com/c/iQrCA3Ma
     */
    let sanitized = text.replace(/^\+([^1])/, "$1");
    const number = new AsYouType("US").input(sanitized);
    const parsedNumber = parsePhoneNumber(number, "US");
    if (parsedNumber?.isValid()) {
      setNumber(parsedNumber ?? null);
    } else {
      setNumber(null);
    }
    setFormattedNumber(number);
  }

  const Checkmark = () => (
    <span>
      <i className="fa-solid fa-circle-check has-text-success"></i>
    </span>
  );

  const getPhoneLayout = () => {
    if (pathname === "/signup/verifyphone" || pathname === "/home/settings/phone") {
      // sign up flow
      return (
        <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
          {showErrorMessage && (
            <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
              {showErrorMessage}
            </ErrorModal>
          )}
          <div className="flex-1 px-2">
            <div className="flex flex-col gap-4">
              <p className="text-center text-lg font-semibold">
                {t("onboarding.verify-enter-phone-desc")}
              </p>
              <div className="relative">
                <input
                  {...rest}
                  className="input input-primary w-full"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={t("onboarding.verify-enter-phone-input")}
                  value={formattedNumber}
                  ref={(el) => {
                    registerRef(el);
                    if (mobileInputRef) mobileInputRef.current = el;
                  }}
                  onChange={(event) => onTextChange(event.target.value)}
                  data-testid="phone-input"
                />
                {number !== null && (
                  <img
                    src="/assets/images/green_status.png"
                    className="w-[20px] h-[20px] absolute right-3 top-1/2 transform -translate-y-1/2"
                  />
                )}
              </div>
              <p className="text-xs">{t("onboarding.verify-enter-phone-small-print-skip")}</p>
              {/* Desktop buttons stay in content flow */}
              <div className="max-md:hidden flex flex-col gap-4">
                <button
                  disabled={submittingPhoneNumber || number === null}
                  className="btn btn-primary"
                  type="submit"
                  data-testid="next-desktop"
                >
                  {submittingPhoneNumber ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    t("generic.next")
                  )}
                </button>
                {pathname === "/signup/verifyphone" && (
                  <button className="btn btn-ghost w-full" onClick={() => navigate("/home/search")}>
                    {t("generic.skip")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Mobile buttons anchored at bottom */}
          <div className="shrink-0 max-md:pb-8 md:hidden flex flex-col gap-4 pt-4 px-2">
            <button
              disabled={submittingPhoneNumber || number === null}
              className="btn btn-primary btn-lg"
              type="submit"
              data-testid="next-mobile"
            >
              {submittingPhoneNumber ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                t("generic.next")
              )}
            </button>
            {pathname === "/signup/verifyphone" && (
              <button
                className="btn btn-lg btn-ghost w-full"
                onClick={() => navigate("/home/search")}
              >
                {t("generic.skip")}
              </button>
            )}
          </div>
        </form>
      );
    } else if (pathname === "/home/settings") {
      // settings update phone
      return (
        <blockquote className="mt-2">
          {showErrorMessage && (
            <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
              {showErrorMessage}
            </ErrorModal>
          )}
          <form onSubmit={handleSubmit(onSendFromSettings)}>
            <div className="">
              <label className="">{t("onboarding.verify-enter-phone-desc")}</label>
              <input
                {...rest}
                ref={registerRef}
                className="input input-primary"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(xxx) xxx-xxxx"
                value={formattedNumber}
                onChange={(event) => onTextChange(event.target.value)}
              />
              {number !== null && (
                <span className="password-toggle home">
                  <Checkmark />
                </span>
              )}
            </div>
            <p className="mt-4 text-xs">{t("onboarding.verify-enter-phone-small-print")}</p>

            <button
              className={`btn mt-5 pl-4 pr-4`}
              type="submit"
              disabled={submittingPhoneNumber || number === null}
            >
              {submittingPhoneNumber ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                t("generic.send")
              )}
            </button>
          </form>
        </blockquote>
      );
    } else {
      throw new Error("Sorry, this is not supported");
    }
  };

  return <>{getPhoneLayout()}</>;
}

export default VerifyPhone;
